import * as Comlink from "comlink";
import * as esbuild from "esbuild-wasm";
import { BuildResult, CompileResult, SassOptions } from "../types";
import { globalExternals } from "@fal-works/esbuild-plugin-global-externals";
import { filesPlugin } from "./esbuild-files-plugin";
import { getEnvironmentVariables, getGlobals } from "./utils";
import { stylesPlugin } from "./esbuild-styles-plugin";
import { Buffer } from "buffer";

self.Buffer = Buffer;

export interface FileSet {
  [filePath: string]: string;
}

export interface ExternalImport {
  name: string;
  globalName?: string;
}

export interface TranspilerOptions {
  entryFilePath: string;
  sourceFiles: FileSet;
  environmentVariables?: { [name: string]: string };
  imports?: ExternalImport[];
  minify?: boolean;
  sourcemap?: boolean;
}

let initialized = false;

const initialize = () => {
  if (initialized)
    return;

  console.time("esbuild initialize");
  return esbuild
    .initialize({
      worker: false,
      wasmURL: "https://unpkg.com/esbuild-wasm@0.17.10/esbuild.wasm",
    })
    .then(() => {
      initialized = true;
      console.timeEnd("esbuild initialize");
    });
}

const transpile = async (options: TranspilerOptions, compileSass?: (contents: string, options: SassOptions) => Promise<CompileResult>): Promise<BuildResult> => {
  console.time("esbuild transpile");

  try {
    const imports = getGlobals(options.imports);
    const result = await esbuild.build({
      entryPoints: [options.entryFilePath],
      bundle: true,
      write: false,
      minify: options.minify,
      format: "esm",
      platform: "browser",
      target: ["es2022"],
      outdir: "/",
      sourcemap: options.sourcemap ? "inline" : undefined,
      sourcesContent: options.sourcemap,
      define: getEnvironmentVariables(options.environmentVariables),
      plugins: [
        globalExternals(imports.globals) as any, // some types of Plugin in esbuild and esbuild-wasm are not compatible
        filesPlugin(options.sourceFiles, imports.external),
        stylesPlugin(options.sourceFiles, compileSass)
      ],
      allowOverwrite: true
    });

    if (result.outputFiles && result.outputFiles.length)
      return {
        content: result.outputFiles[0].text
      };
    else
      throw new Error("No build output");

  } catch (error) {
    const buildResult = error as esbuild.BuildResult;
    if (buildResult && buildResult.errors) {
      return {
        errors: buildResult.errors.map((message) => ({
          text: message.text,
          file: message.location?.file,
          lineText: message.location?.lineText,
          line: message.location?.line,
          column: message.location?.column
        }))
      };
    }
    if (error instanceof Error) {
      return {
        errors: [{
          text: error.message
        }]
      }
    }

    return {
      errors: [{
        text: `Unexpected build error occured: ${JSON.stringify(error)}`
      }]
    }

  } finally {
    console.timeEnd("esbuild transpile");
  }
}

const expose = {
  initialize,
  transpile
}

export type TranspileWorker = typeof expose;

Comlink.expose(expose);